import { createConsumer } from '@rails/actioncable'
import type { LessonUpdate, CableMessage } from '@/types'

/**
 * WebSocket service using ActionCable client with AnyCable backend
 *
 * In development: WebSocket features may be disabled if AnyCable-Go is not running
 * In production: Should connect to AnyCable-Go WebSocket server
 *
 * All methods gracefully handle missing WebSocket connection
 */
class WebSocketService {
  private cable: any = null
  private subscriptions: Map<string, any> = new Map()
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 1000
  private processedEvents: Set<string> = new Set() // For event deduplication

  constructor() {
    this.initializeCable()
    // Clean up old processed events every 5 minutes to prevent memory leaks
    setInterval(() => {
      this.processedEvents.clear()
      // Clean up window-based deduplication tracking
      delete (window as any).__lastProcessedEvent
      delete (window as any).__lastProcessedHostEvent
    }, 5 * 60 * 1000)
  }

  reinitialize() {
    this.unsubscribeAll()
    this.cable = null
    this.reconnectAttempts = 0
    this.initializeCable()
  }

  private initializeCable() {
    if (typeof window !== 'undefined') {
      try {
        // Get auth token for WebSocket authentication (optional — guest players connect without one)
        const authToken = localStorage.getItem('auth_token')

        if (authToken) {
          if (authToken.length < 10) {
            // Auth token appears invalid (too short)
          }
        }

        // Create ActionCable consumer - connect to Rails ActionCable server
        let wsUrl = import.meta.env.DEV
          ? 'ws://localhost:3000/cable'
          : undefined  // Use default in production

        // Add auth token as URL parameter when available
        if (wsUrl && authToken) {
          wsUrl += `?token=${encodeURIComponent(authToken)}`
        }

        try {
          this.cable = createConsumer(wsUrl)
        } catch (error) {
          this.cable = null
          return
        }

        if (this.cable) {
          try {
            const hasConnection = !!this.cable.connection
            if (hasConnection) {
              if (this.cable.connection) {
                const connection = this.cable.connection
                connection.onopen = () => {
                  this.reconnectAttempts = 0 // Reset reconnect attempts on successful connection
                }
                connection.onclose = (event: any) => {
                  if (event.code !== 1000) { // Not normal closure
                    this.handleReconnection()
                  }
                }
                connection.onerror = () => {}
              }
            }
          } catch (e) {}
        }

        this.setupConnectionHandlers()

        // Immediate connection state check
        setTimeout(() => {
          // Connection state checked silently
        }, 1000)
      } catch (error) {
        this.cable = null
      }
    }
  }

  private setupConnectionHandlers() {
    if (!this.cable) return

    // Add a connection check after a delay to allow connection to establish
    setTimeout(() => {
      if (this.cable?.connection) {
        const isOpen = this.cable.connection?.isOpen?.()
        if (!isOpen) {
          this.handleReconnection()
        }
      }
    }, 5000) // Increased timeout to allow more time for connection
  }

  private handleReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      setTimeout(() => {
        this.cable?.connection?.open?.()
      }, this.reconnectInterval * this.reconnectAttempts)
    }
  }

  // Subscribe to lesson updates (for authenticated host/players)
  subscribeToLesson(
    lessonId: string,
    callbacks: {
      onLessonUpdate?: (update: LessonUpdate) => void
      onPlayerJoined?: (player: any) => void
      onPlayerLeft?: (player: any) => void
      onActivityStarted?: (activity: any) => void
      onResponseSubmitted?: (response: any) => void
      onLessonStateChange?: (state: any) => void
      onError?: (error: any) => void
    }
  ): () => void {
    if (!this.cable) {
      return () => {}
    }

    const channelName = `LessonChannel_${lessonId}`

    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromLesson(lessonId)
    }

    const subscription = this.cable.subscriptions.create(
      {
        channel: 'LessonChannel',
        lesson_id: lessonId
      },
      {
        connected() {
          // Request initial lesson state
          this.perform('ping')
        },

        disconnected() {
        },

        received(data: any) {

          // Create event key for deduplication (simple approach for now)
          const activityId = data.activity?.id || data.activity_id
          const eventKey = `${data.type}_${activityId || 'no-activity'}_${data.timestamp || Date.now()}`

          // Simple deduplication - check if we've seen this exact event recently
          if ((window as any).__lastProcessedEvent === eventKey) {
            return
          }
          (window as any).__lastProcessedEvent = eventKey

          switch (data.type) {
            case 'lesson_state':
              callbacks.onLessonStateChange?.(data.data)
              break
            case 'player_connected':
            case 'player_joined':
              callbacks.onPlayerJoined?.(data.data)
              break
            case 'player_disconnected':
            case 'player_left':
            case 'player_kicked':
              callbacks.onPlayerLeft?.(data.data)
              break
            case 'activity_started':
            case 'activity_paused':
            case 'activity_resumed':
              // Handle activity state changes
              callbacks.onActivityStarted?.(data.activity || data.data)
              break
            case 'activity_completed':
            case 'activity_auto_completed':
              // Handle activity completion - clear current activity
              callbacks.onActivityStarted?.(null)
              break
            case 'activity_status_changed':
              // Handle activity status changes (start, pause, resume, complete)
              callbacks.onActivityStarted?.(data.activity)
              break
            case 'activity_time_extended':
            case 'activity_timer_reset':
              // Handle timer changes - update activity data
              callbacks.onActivityStarted?.(data.activity)
              break
            case 'slide_changed':
              // slide_changed is handled at the store level via onActivityStarted fallback
              // or directly via the store's guest channel handler
              break
            case 'response_submitted':
            case 'response_confirmed':
              callbacks.onResponseSubmitted?.(data.data)
              break
            case 'error':
              callbacks.onError?.(data.data)
              break
            case 'pong':
              // Heartbeat response - connection is alive
              break
            default:
              callbacks.onLessonUpdate?.(data as LessonUpdate)
          }
        },

        // Methods to send data to the server
        joinLesson(data = {}) {
          this.perform('join_lesson', data)
        },

        leaveLesson(data = {}) {
          this.perform('leave_lesson', data)
        },

        submitResponse(data: any) {
          this.perform('submit_response', data)
        },

        ping() {
          this.perform('ping')
        },

        heartbeat() {
          this.perform('heartbeat')
        }
      }
    )

    this.subscriptions.set(channelName, subscription)

    // Return unsubscribe function
    return () => this.unsubscribeFromLesson(lessonId)
  }

  // Subscribe to guest lesson channel (for anonymous players)
  subscribeToGuestLesson(
    runCode: string,
    sessionId: string,
    callbacks: {
      onLessonStateUpdate?: (state: any) => void
      onPlayerStatusChange?: (status: any) => void
      onPlayerDataUpdate?: (player: any) => void
      onError?: (error: any) => void
    }
  ): () => void {
    if (!this.cable) {
      return () => {}
    }

    const channelName = `GuestLessonChannel_${runCode}`

    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromChannel(channelName)
    }

    const subscription = this.cable.subscriptions.create(
      {
        channel: 'GuestLessonChannel',
        run_code: runCode,
        session_id: sessionId
      },
      {
        connected() {
          // Send heartbeat to get initial state
          this.perform('heartbeat', { request_state: true })
        },

        disconnected() {
        },

        received(data: any) {

          switch (data.type) {
            case 'lesson_state_update':
              callbacks.onLessonStateUpdate?.(data.data)
              // Also update player data if included
              if (data.data?.player) {
                callbacks.onPlayerDataUpdate?.(data.data.player)
              }
              break
            case 'player_status_change':
              callbacks.onPlayerStatusChange?.(data.data)
              break
            case 'player_data_updated':
              callbacks.onPlayerDataUpdate?.(data.player)
              break
            case 'activity_started':
            case 'activity_paused':
            case 'activity_resumed':
            case 'activity_status_changed':
            case 'activity_time_extended':
            case 'activity_timer_reset':
              // Handle activity events for guest players
              callbacks.onLessonStateUpdate?.({
                current_activity: data.activity || data.data,
                lesson: null, // Don't override lesson data
                players: null // Don't override players data
              })
              break
            case 'activity_completed':
            case 'activity_auto_completed':
              // Handle activity completion - clear current activity for guest players
              callbacks.onLessonStateUpdate?.({
                current_activity: null,
                lesson: null, // Don't override lesson data
                players: null // Don't override players data
              })
              break
            case 'slide_changed':
              callbacks.onLessonStateUpdate?.({
                slide_changed: { page: data.page },
                lesson: null,
                players: null
              })
              break
            case 'response_submitted':
              // Another player submitted a response — no action needed for the guest view
              break
            case 'error':
              callbacks.onError?.(data.data)
              break
            default:
              break
          }
        },

        // Guest lesson actions
        heartbeat(data = {}) {
          this.perform('heartbeat', data)
        }
      }
    )

    this.subscriptions.set(channelName, subscription)

    return () => this.unsubscribeFromChannel(channelName)
  }

  // Subscribe to lesson host channel (for lesson hosts/facilitators)
  // Perform an action on the host channel subscription
  performHostAction(lessonId: string, action: string, data: any = {}) {
    const channelName = `LessonHostChannel_${lessonId}`
    const subscription = this.subscriptions.get(channelName)
    if (subscription) {
      subscription.perform(action, data)
    }
  }

  subscribeToLessonHost(
    lessonId: string,
    callbacks: {
      onHostLessonState?: (state: any) => void
      onLessonLifecycleEvent?: (event: any) => void
      onActivityEvent?: (event: any) => void
      onSlideChanged?: (data: any) => void
      onRealTimeAnalytics?: (analytics: any) => void
      onPlayerResponse?: (response: any) => void
      onPlayerJoined?: (player: any) => void
      onPlayerLeft?: (player: any) => void
      onError?: (error: any) => void
    }
  ): () => void {
    if (!this.cable) {
      // Return a no-op unsubscribe function to prevent errors
      return () => {}
    }

    const channelName = `LessonHostChannel_${lessonId}`

    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromChannel(channelName)
    }

    const subscription = this.cable.subscriptions.create(
      {
        channel: 'LessonHostChannel',
        lesson_id: lessonId
      },
      {
        connected() {
        },

        disconnected() {
        },

        rejected() {
        },

        received(data: any) {

          // Create event key for deduplication for host events
          const activityId = data.activity?.id || data.activity_id
          const eventKey = `host_${data.type}_${activityId || 'no-activity'}_${data.timestamp || Date.now()}`

          // Simple deduplication - check if we've seen this exact event recently
          if ((window as any).__lastProcessedHostEvent === eventKey) {
            return
          }
          (window as any).__lastProcessedHostEvent = eventKey

          switch (data.type) {
            case 'host_lesson_state':
              callbacks.onHostLessonState?.(data.data)
              break
            case 'lesson_started':
            case 'lesson_paused':
            case 'lesson_resumed':
            case 'lesson_completed':
              callbacks.onLessonLifecycleEvent?.(data)
              break
            case 'activity_started':
            case 'host_activity_started':
            case 'activity_completed':
            case 'host_activity_completed':
            case 'host_activity_auto_completed':
              callbacks.onActivityEvent?.(data)
              break
            case 'host_activity_status_changed':
              // Handle host-specific activity status changes
              callbacks.onActivityEvent?.({
                type: 'activity_status_changed',
                activity: data.activity,
                timestamp: data.timestamp
              })
              break
            case 'host_activity_time_extended':
              // Handle activity time extensions
              callbacks.onActivityEvent?.({
                type: 'activity_time_extended',
                activity: data.activity,
                extended_minutes: data.extended_minutes,
                timestamp: data.timestamp
              })
              break
            case 'host_activity_timer_reset':
              // Handle activity timer resets
              callbacks.onActivityEvent?.({
                type: 'activity_timer_reset',
                activity: data.activity,
                timestamp: data.timestamp
              })
              break
            case 'slide_changed':
              callbacks.onSlideChanged?.(data)
              break
            case 'real_time_analytics':
              callbacks.onRealTimeAnalytics?.(data.data)
              break
            case 'host_player_response':
              // Handle new player response for LessonMaster
              callbacks.onPlayerResponse?.(data.response)
              break
            case 'response_submitted':
              // Handle response submissions for host
              callbacks.onPlayerResponse?.(data)
              break
            case 'player_connected':
            case 'player_joined':
              // Handle player join events for host
              callbacks.onPlayerJoined?.(data)
              break
            case 'player_disconnected':
            case 'player_left':
              // Handle player leave events for host
              callbacks.onPlayerLeft?.(data)
              break
            case 'player_kicked':
              // Handle player kick events for host (with full player details)
              callbacks.onPlayerLeft?.(data)
              break
            case 'error':
              callbacks.onError?.(data.data)
              break
            default:
              break
          }
        },

        // Host control actions
        startLesson(data = {}) {
          this.perform('start_lesson', data)
        },

        pauseLesson(data = {}) {
          this.perform('pause_lesson', data)
        },

        resumeLesson(data = {}) {
          this.perform('resume_lesson', data)
        },

        completeLesson(data = {}) {
          this.perform('complete_lesson', data)
        },

        startActivity(activityId: string, data = {}) {
          this.perform('start_activity', { activity_id: activityId, ...data })
        },

        completeActivity(activityId: string, data = {}) {
          this.perform('complete_activity', { activity_id: activityId, ...data })
        },

        kickPlayer(playerId: string) {
          this.perform('kick_player', { player_id: playerId })
        },

        getRealTimeAnalytics() {
          this.perform('get_real_time_analytics')
        },

        moderateResponse(responseId: string, action: string) {
          this.perform('moderate_response', { response_id: responseId, action })
        }
      }
    )

    this.subscriptions.set(channelName, subscription)

    return () => this.unsubscribeFromChannel(channelName)
  }

  // Subscribe to analytics updates
  subscribeToAnalytics(
    lessonId: string,
    callbacks: {
      onAnalyticsUpdate?: (analytics: any) => void
      onRealtimeUpdate?: (data: any) => void
      onPeriodicUpdate?: (data: any) => void
      onError?: (error: any) => void
    }
  ): () => void {
    if (!this.cable) {
      return () => {}
    }

    const channelName = `AnalyticsChannel_${lessonId}`

    if (this.subscriptions.has(channelName)) {
      this.unsubscribeFromChannel(channelName)
    }

    const subscription = this.cable.subscriptions.create(
      {
        channel: 'AnalyticsChannel',
        lesson_id: lessonId
      },
      {
        connected: function() {
          // Request initial data
          this.perform('request_update')
        },

        disconnected: () => {
        },

        received: (data: any) => {

          switch (data.type) {
            case 'analytics_update':
              callbacks.onAnalyticsUpdate?.(data.data)
              break
            case 'realtime_update':
              callbacks.onRealtimeUpdate?.(data.data)
              break
            case 'periodic_update':
              callbacks.onPeriodicUpdate?.(data.data)
              break
            default:
              break
          }
        },

        // Methods to request data
        requestUpdate: function() {
          this.perform('request_update')
        },

        requestRealtimeData: function() {
          this.perform('request_realtime_data')
        }
      }
    )

    this.subscriptions.set(channelName, subscription)

    return () => this.unsubscribeFromChannel(channelName)
  }

  // Unsubscribe from specific lesson
  unsubscribeFromLesson(lessonId: string) {
    const channelName = `LessonChannel_${lessonId}`
    this.unsubscribeFromChannel(channelName)
  }

  // Unsubscribe from specific channel
  private unsubscribeFromChannel(channelName: string) {
    const subscription = this.subscriptions.get(channelName)
    if (subscription) {
      subscription.unsubscribe()
      this.subscriptions.delete(channelName)
    }
  }

  // Unsubscribe from all channels
  unsubscribeAll() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })
    this.subscriptions.clear()
  }

  // Get connection status
  isConnected(): boolean {
    return this.cable?.connection?.isOpen?.() || false
  }

  // Manually reconnect
  reconnect() {
    if (this.cable?.connection) {
      this.cable.connection.close?.()
      this.cable.connection.open?.()
    }
  }

  // Cleanup
  disconnect() {
    this.unsubscribeAll()
    if (this.cable) {
      this.cable.disconnect()
      this.cable = null
    }
  }
}

// Singleton instance
export const webSocketService = new WebSocketService()

